import React, { useState, useRef, useEffect } from "react";
import DOMPurify from "dompurify";
import { useSelector, useDispatch } from "react-redux";
import { renderAsync } from "docx-preview";
import * as XLSX from "xlsx";
import {
	iconMap,
	shortDateFormat,
	getDocumentPreviewType,
} from "../utilities/utilities";
import ButtonLink from "./ButtonLink";
import { Drawer, Loader } from "@mantine/core";
import FavoriteStar from "./FavoriteStar";
import DocumentAIPane from "./DocumentAIPane";
import ConversationActions from "./ConversationActions";
import { ButtonWithIcon } from "./ButtonWithIcon";
import ButtonWithPopoverFeedback from "./ButtonWithPopoverFeedback";
import MaterialThreeDotMenu from "./MaterialThreeDotMenu";
import config from "../config/legalworks.frontend.config";
import {
	IconCopy,
	IconDownload,
	IconExternalLink,
	IconInfoCircle,
	IconPencil,
} from "@tabler/icons-react";
import {
	getContentForDocumentPreview,
	getSignedUrlForDocumentAndDownload,
	setFavorite,
	openDocumentInLauncher,
} from "../actions/documentActions";
import {
	getSignedUrlForEmailAndDownload,
	updateEmailNote,
} from "../actions/emailActions";
import { openGlobalDialog } from "../actions/globalEditDialogActions";
import {
	TRASH_LIFECYCLE_STATUS,
	ARCHIVE_LIFECYCLE_STATUS,
	getDisabledStateFromLifecycleStatuses,
} from "../utilities/lifecycleStatus";

const sanitizePreviewTextContent = (documentPreviewResponse) => {
	/*
		The document contents comes from the search index,
		which as of 2024 is extracted via Apache Tika.  These replacements
		were decided on based on what was seen from some PDFs and DOCX files
	*/

	var sanitizedContent = documentPreviewResponse.content.trim();

	sanitizedContent = sanitizedContent.replace(
		/\[[\bbookmark: _GoBack\b^\]]*\]/g,
		""
	); // This regex will only replace "[bookmark: _GoBack]"
	sanitizedContent = sanitizedContent.replace(
		/[^a-zA-Z0-9`!@#$%^&*()_+|\-=\\{}[\]:"";'<>?,.\w\sèìòùÈÌÒÙéíóúýêîôûÊÎÔÛñõÑÕëïöüÿËÏÖÜŸ/]/gi,
		""
	); // This will remove all the characters which are not included in the given list.
	sanitizedContent = sanitizedContent.replace(
		new RegExp(/^\s*\n{2,}/gm),
		"\n"
	); // This will replace multiple \n into single \n.Also replace single \n into \n and 0 or more whitespace characters
	sanitizedContent = sanitizedContent.replace(new RegExp(/^\s*Sheet1/gm), "");
	return sanitizedContent;
};

const Loading = () => {
	return (
		<div style={{ marginTop: 200 }}>
			<center>
				<Loader size={28} />
			</center>
		</div>
	);
};

const RightPaneHeader = ({
	leftPart,
	middlePart,
	buttonsArray,
	dropdownItems,
	closePane,
}) => {
	return (
		<div className="preview-header">
			<div className="preview-header-left">{leftPart}</div>
			<div style={{ marginLeft: "auto", display: "flex", flexShrink: 0 }}>
				{middlePart ? (
					<div
						style={{
							padding: 15,
							textWrap: "nowrap",
						}}
					>
						{middlePart}
					</div>
				) : null}
				<div className="preview-header-right">
					<div className="preview-icon-box">
						<div>
							<MaterialThreeDotMenu
								position={"relative"}
								menuItems={[
									...dropdownItems,
									{ divider: true },
									{ text: "Close", onSelect: closePane },
								]}
							/>
						</div>
						<div className="preview-buttons">
							{buttonsArray.map((buttonComponent, index) => (
								<div key={index}>{buttonComponent}</div>
							))}
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

/*
	There are 2 document contexts for the right pane:
		- document preview
		- document AI conversation

	They have different buttons and dropdown menu options, but the same filename
	and favorite star functionality
*/
const DocumentHeader = ({
	filename,
	versionLabel,
	setFavorite,
	isFavorite,
	favoriteLoading,
	inTrashOrArchive,
	inTrashOrArchiveDisabledText,
	buttonsArray,
	...restProps
}) => {
	return (
		<RightPaneHeader
			leftPart={
				<div style={{ display: "flex", alignItems: "center" }}>
					{documentPreviewFileIcon({ filename, type: "File" })}

					<span
						style={{
							overflow: "hidden",
							textOverflow: "ellipsis",
							whiteSpace: "nowrap",
						}}
					>
						{filename}
					</span>
					{versionLabel ? (
						<span
							className="tagPill"
							style={{
								marginLeft: "auto",
								marginRight: 12,
								width: "auto",
								flexShrink: 0,
							}}
						>
							{versionLabel}
						</span>
					) : null}
				</div>
			}
			buttonsArray={[
				<ButtonWithIcon
					handler={setFavorite}
					disabled={favoriteLoading || inTrashOrArchive}
					style={{ paddingLeft: 10, paddingRight: 10 }}
					title={
						inTrashOrArchive
							? inTrashOrArchiveDisabledText
							: isFavorite
								? "Un-Favorite this Document"
								: "Favorite this Document"
					}
					iconComponent={
						<FavoriteStar
							isFavorite={isFavorite}
							style={{
								fontSize: "1.2em",
							}}
						/>
					}
					key="favoriteButton"
				/>,
				...buttonsArray,
			]}
			{...restProps}
		/>
	);
};

const DocumentPreviewHeader = ({
	openProperties,
	openDocument,
	onTogglePlainText,
	toggleLabel,
	...restProps
}) => {
	const buttons = [
		restProps.docCanBeOpenedInLauncher ? (
			<ButtonWithIcon
				handler={openDocument}
				title={"Open this Document"}
				iconComponent={
					<IconExternalLink
						size={16}
						style={{ verticalAlign: "middle" }}
					/>
				}
				buttonText="Open Document"
				key="openDocumentButton"
			/>
		) : (
			<ButtonWithIcon
				handler={restProps.downloadCopyOfDocument}
				title={"Download a Copy of this Document"}
				iconComponent={
					<IconDownload
						size={16}
						style={{ verticalAlign: "middle" }}
					/>
				}
				buttonText="Download a Copy"
				key="openDocumentButton"
			/>
		),
	];

	if (onTogglePlainText) {
		buttons.push(
			<ButtonWithIcon
				handler={onTogglePlainText}
				title={toggleLabel}
				buttonText={toggleLabel}
				key="togglePlainTextButton"
			/>
		);
	}

	return (
		<DocumentHeader
			buttonsArray={buttons}
			dropdownItems={[
				{
					text: "Document Properties",
					onSelect: openProperties,
				},
			]}
			{...restProps}
		/>
	);
};
const DocumentAIHeader = ({
	openProperties,
	openDocument,
	headerExtraInfoDisplay,
	headerExtraInfoTooltip,
	conversationActionItems,
	...restProps
}) => {
	return (
		<DocumentHeader
			middlePart={
				headerExtraInfoDisplay ? (
					<div style={{ display: "flex" }}>
						<div>{headerExtraInfoDisplay}</div>
						{headerExtraInfoTooltip ? (
							<div title={headerExtraInfoTooltip}>
								<IconInfoCircle
									size={20}
									style={{
										verticalAlign: "sub",
									}}
								/>
							</div>
						) : null}
					</div>
				) : null
			}
			buttonsArray={[]}
			dropdownItems={[
				restProps.docCanBeOpenedInLauncher
					? {
							text: "Open Document",
							onSelect: openDocument,
						}
					: {
							text: "Download a Copy",
							onSelect: restProps.downloadCopyOfDocument,
						},
				{
					text: "Document Properties",
					onSelect: openProperties,
				},
				...(conversationActionItems?.length
					? [{ divider: true }, ...conversationActionItems]
					: []),
			]}
			{...restProps}
		/>
	);
};
const EmailHeader = ({
	subjectline,
	originalFilename,
	openEmailShortNoteEditor,
	openEmail,
	isEditNoteDisabled,
	disabledTextForShortNote,
	shortNoteValue,
	...restProps
}) => {
	return (
		<RightPaneHeader
			leftPart={
				<div>
					{documentPreviewFileIcon({
						filename: originalFilename,
						type: "Email",
					})}

					<span>{subjectline}</span>
				</div>
			}
			buttonsArray={[
				<ButtonWithIcon
					handler={() => {
						openEmail();
					}}
					key="downloadEmailButton"
					buttonText="Download Email"
					iconComponent={
						<IconDownload
							size={16}
							style={{ verticalAlign: "middle" }}
						/>
					}
					title="Download this Email"
				/>,
			]}
			dropdownItems={[
				{
					text: shortNoteValue
						? "Edit Email Short Note"
						: "Add Email Short Note",
					disabled: isEditNoteDisabled,
					disabledHoverText: disabledTextForShortNote,
					onSelect: () => {
						console.log(
							"active element is",
							document.activeElement
						);

						openEmailShortNoteEditor();
						document.activeElement.blur();
					},
				},
			]}
			{...restProps}
		/>
	);
};
const MatterHeader = ({ name, openMatter, ...restProps }) => {
	return (
		<RightPaneHeader
			leftPart={
				<div>
					{documentPreviewFileIcon({
						filename: name,
						type: "Matter",
					})}

					<span>{name}</span>
				</div>
			}
			buttonsArray={[
				<ButtonWithIcon
					handler={openMatter}
					key="goToMatterButton"
					buttonText="Go to Matter"
					iconComponent={
						<IconExternalLink
							size={16}
							style={{ verticalAlign: "middle" }}
						/>
					}
					title="Go to Matter"
				/>,
			]}
			dropdownItems={[]}
			{...restProps}
		/>
	);
};

const MatterPreviewCopyButton = ({ copyValue }) => (
	<ButtonWithPopoverFeedback
		className="matterPreviewFieldCopyButton"
		iconComponent={<IconCopy size={14} />}
		onClick={() => navigator.clipboard.writeText(String(copyValue))}
		feedbackMessage="Copied to clipboard!"
		duration={800}
		title="Copy to clipboard"
	/>
);

const MatterPreviewField = ({ label, copyValue, children }) => (
	<div className="matterPreviewField">
		<label className="matterPreviewFieldLabel">{label}</label>
		<div className="matterPreviewFieldValue">
			{children}
			{copyValue ? (
				<MatterPreviewCopyButton copyValue={copyValue} />
			) : null}
		</div>
	</div>
);

const MatterPreviewPillGroup = ({ items, itemKey, itemLabel, copyValue }) => (
	<div className="matterPreviewPillGroup">
		{items.map((item) => (
			<span
				className="tagPill"
				style={{ width: "auto" }}
				key={itemKey(item)}
			>
				{itemLabel(item)}
			</span>
		))}
		{copyValue ? <MatterPreviewCopyButton copyValue={copyValue} /> : null}
	</div>
);

const generateMatterPreviewContent = (matter) => {
	if (!matter) {
		return (
			<div className="previewOpenDocumentContainer">
				<div className="previewOpenDocumentCenter">
					<p>Preview Not Available</p>
				</div>
			</div>
		);
	}

	const openedDateText = matter.dateOpened
		? shortDateFormat(matter.dateOpened)
		: "";
	const closedDateText = matter.dateClosed
		? shortDateFormat(matter.dateClosed)
		: "";
	const assignedStaffText = matter.assignedStaff?.length
		? matter.assignedStaff
				.map((staffMember) => staffMember.displayName)
				.join(", ")
		: "";
	const tagsText = matter.tags?.length
		? matter.tags.map((tag) => tag.tagName).join(", ")
		: "";

	return (
		<div>
			<MatterPreviewField label="Matter ID" copyValue={matter.id}>
				{matter.id}
			</MatterPreviewField>
			<MatterPreviewField label="Matter Name" copyValue={matter.name}>
				{matter.name}
			</MatterPreviewField>
			<MatterPreviewField label="Client" copyValue={matter.clientName}>
				{matter.clientID ? (
					<a
						href={`/clients/${matter.clientID}/`}
						target="_blank"
						rel="noopener noreferrer"
					>
						{matter.clientName}
					</a>
				) : (
					matter.clientName
				)}
			</MatterPreviewField>
			<MatterPreviewField
				label="Custom Matter ID"
				copyValue={matter.customMatterID}
			>
				{matter.customMatterID}
			</MatterPreviewField>
			<MatterPreviewField
				label="Matter Type"
				copyValue={matter.matterType}
			>
				{matter.matterType}
			</MatterPreviewField>
			<MatterPreviewField
				label="Location"
				copyValue={matter.officeBranch}
			>
				{matter.officeBranch}
			</MatterPreviewField>
			<MatterPreviewField label="Opened Date" copyValue={openedDateText}>
				{openedDateText}
			</MatterPreviewField>
			<MatterPreviewField label="Closed Date" copyValue={closedDateText}>
				{closedDateText}
			</MatterPreviewField>
			<MatterPreviewField
				label="Description"
				copyValue={matter.description}
			>
				{matter.description}
			</MatterPreviewField>
			<MatterPreviewField
				label="Assigned Staff"
				copyValue={assignedStaffText}
			>
				{assignedStaffText || null}
			</MatterPreviewField>
			<MatterPreviewField label="Tags">
				{matter.tags?.length ? (
					<MatterPreviewPillGroup
						items={matter.tags}
						itemKey={(tag) => tag.tagID}
						itemLabel={(tag) => tag.tagName}
						copyValue={tagsText}
					/>
				) : null}
			</MatterPreviewField>
		</div>
	);
};

const NoteHeader = ({ name, openEditMatterNote, ...restProps }) => {
	return (
		<RightPaneHeader
			leftPart={
				<div>
					{documentPreviewFileIcon({
						filename: name,
						type: "Note",
					})}

					<span>{name}</span>
				</div>
			}
			buttonsArray={[
				<ButtonWithIcon
					handler={() => {
						openEditMatterNote();
					}}
					key="editNoteButton"
					buttonText="Edit Note"
					iconComponent={
						<IconPencil
							size={16}
							style={{ verticalAlign: "middle" }}
						/>
					}
					title="Edit Note"
				/>,
			]}
			dropdownItems={[]}
			{...restProps}
		/>
	);
};

function documentPreviewFileIcon({ filename, type }) {
	var rowFileName = {
		original: {
			filename: filename,
			type,
		},
	};

	if (filename) {
		const { iconComponent } = iconMap(rowFileName);
		return <>{iconComponent}</>;
	}
}

const SlidingPaneWrapper = ({
	isOpen,
	onRequestClose,
	zIndex = 50,
	children,
}) => {
	return (
		<Drawer
			opened={isOpen}
			onClose={onRequestClose}
			position="right"
			size="900px"
			withCloseButton={false}
			zIndex={zIndex}
			overlayProps={{ top: 72 }}
			styles={{
				inner: { top: 72 },
				content: {
					display: "flex",
					flexDirection: "column",
					overflowX: "hidden",
					overflowY: "hidden",
				},
				body: {
					padding: 0,
					height: "100%",
					display: "flex",
					flexDirection: "column",
					overflow: "hidden",
				},
			}}
		>
			{children}
		</Drawer>
	);
};

const DocumentAISlidingPane = ({
	isOpen,
	onRequestClose,
	filename,
	closePane,
	openProperties,
	openDocument,
	isFavorite,
	setFavorite,
	favoriteLoading,
	inTrashOrArchive,
	inTrashOrArchiveDisabledText,
	headerExtraInfoTooltip,
	headerExtraInfoDisplay,
	documentID,
	setHeaderExtraInfo,
	downloadCopyOfDocument,
	docCanBeOpenedInLauncher,
	matterID,
}) => {
	const dispatch = useDispatch();
	const [messages, setMessages] = useState([]);

	const conversationActionItems = ConversationActions({
		messages,
		matterID,
		dispatch,
		getNoteTitle: () =>
			`${filename} - AI Conversation on ${new Date().toLocaleString()}`,
	});

	return (
		<SlidingPaneWrapper isOpen={isOpen} onRequestClose={onRequestClose}>
			<div>
				<DocumentAIHeader
					filename={filename}
					closePane={closePane}
					openProperties={openProperties}
					openDocument={openDocument}
					downloadCopyOfDocument={downloadCopyOfDocument}
					docCanBeOpenedInLauncher={docCanBeOpenedInLauncher}
					isFavorite={isFavorite}
					setFavorite={setFavorite}
					favoriteLoading={favoriteLoading}
					inTrashOrArchive={inTrashOrArchive}
					inTrashOrArchiveDisabledText={inTrashOrArchiveDisabledText}
					headerExtraInfoDisplay={headerExtraInfoDisplay}
					headerExtraInfoTooltip={headerExtraInfoTooltip}
					conversationActionItems={conversationActionItems}
				/>
			</div>
			<DocumentAIPane
				documentID={documentID}
				setHeaderExtraInfo={setHeaderExtraInfo}
				messagesChanged={setMessages}
				openDocument={openDocument}
				downloadCopyOfDocument={downloadCopyOfDocument}
				docCanBeOpenedInLauncher={docCanBeOpenedInLauncher}
			/>
		</SlidingPaneWrapper>
	);
};

function DocxPreview({ url }) {
	const containerRef = useRef(null);

	useEffect(() => {
		if (!url || !containerRef.current) return;
		let cancelled = false;
		fetch(url)
			.then((res) => res.arrayBuffer())
			.then((buffer) => {
				if (cancelled || !containerRef.current) return;
				containerRef.current.innerHTML = "";
				return renderAsync(buffer, containerRef.current, null, {
					ignoreLastRenderedPageBreak: false,
				});
			});
		return () => {
			cancelled = true;
		};
	}, [url]);

	return <div ref={containerRef} className="preview-docx" />;
}

function ExcelPreview({ url }) {
	const [sheets, setSheets] = useState([]);
	const [activeSheet, setActiveSheet] = useState(0);

	useEffect(() => {
		if (!url) return;
		fetch(url)
			.then((res) => res.arrayBuffer())
			.then((buffer) => {
				const workbook = XLSX.read(buffer, { type: "array" });
				const parsed = workbook.SheetNames.map((name) => ({
					name,
					html: XLSX.utils.sheet_to_html(workbook.Sheets[name]),
				}));
				setSheets(parsed);
				setActiveSheet(0);
			});
	}, [url]);

	if (sheets.length === 0) return null;

	return (
		<div className="preview-excel">
			{sheets.length > 1 ? (
				<div className="preview-excel-tabs">
					{sheets.map((sheet, i) => (
						<button
							key={sheet.name}
							className={`preview-excel-tab${i === activeSheet ? " active" : ""}`}
							onClick={() => setActiveSheet(i)}
						>
							{sheet.name}
						</button>
					))}
				</div>
			) : null}
			<div
				className="preview-excel-content"
				dangerouslySetInnerHTML={{ __html: sheets[activeSheet].html }}
			/>
		</div>
	);
}

function DocumentSlidingPane({
	openPane,
	selectedRowData,
	closeRightPane,
	directoryPath,
	aiMode,
	matterID,
	showVersion,
	zIndex,
}) {
	const dispatch = useDispatch();
	const documentPreview = useSelector((s) => s.document.documentPreview);
	const previewLoading = useSelector(
		(s) => s.document.documentPreviewLoading
	);
	const launcherInstalled = useSelector((s) => s.global.launcherInstalled);
	const cognitoSub = useSelector((s) => s.user.cognitoUser.attributes.sub);
	const favoriteLoadingFailed = useSelector(
		(s) => s.document.favoriteLoadingFailed
	);
	const [headerExtraInfoDisplay, setHeaderExtraInfoDisplay] = useState("");
	const [headerExtraInfoTooltip, setHeaderExtraInfoTooltip] = useState("");
	const [localIsFavorite, setLocalIsFavorite] = useState(null);
	const [favoriteLoading, setFavoriteLoading] = useState(false);

	useEffect(() => {
		setLocalIsFavorite(null);
	}, [selectedRowData?.id]);

	useEffect(() => {
		if (favoriteLoadingFailed) setLocalIsFavorite(null);
	}, [favoriteLoadingFailed]);

	const isFavorite =
		localIsFavorite !== null
			? localIsFavorite
			: (documentPreview?.isFavorite ??
				selectedRowData?.isFavorite ??
				false);

	const handleSetFavorite = () => {
		if (favoriteLoading) return;
		const newValue = !isFavorite;
		setLocalIsFavorite(newValue);
		setFavoriteLoading(true);
		dispatch(
			setFavorite(
				selectedRowData.id,
				newValue,
				matterID,
				directoryPath,
				selectedRowData
			)
		).finally(() => setFavoriteLoading(false));
	};

	const setHeaderExtraInfo = ({ display, tooltip }) => {
		setHeaderExtraInfoDisplay(display);
		setHeaderExtraInfoTooltip(tooltip);
	};

	const togglePlainText = () => {
		const { id, versionNumber, versionID, filename } = selectedRowData;
		const showingRich =
			documentPreview &&
			(documentPreview.pdfUrl ||
				documentPreview.docxUrl ||
				documentPreview.excelUrl);
		if (showingRich) {
			dispatch(
				getContentForDocumentPreview({
					documentID: id,
					versionNumber,
					previewType: "Document",
				})
			);
		} else {
			const previewTypeMap = {
				PDF_INLINE: "PDF",
				DOCX_INLINE: "Docx",
				EXCEL_INLINE: "Excel",
			};
			const type =
				previewTypeMap[getDocumentPreviewType(filename)] || "Document";
			dispatch(
				getContentForDocumentPreview({
					documentID: id,
					versionID,
					previewType: type,
				})
			);
		}
	};

	// find the lifecycle status of the document preview, or if not available, the selected row data
	const matterOrFolderLifecycleStatusObject =
		documentPreview?.matterOrFolderLifecycleStatusObject ??
			selectedRowData?.matterOrFolderLifecycleStatusObject ?? {
				IN_TRASH: false,
				disabled: false,
				disabledText: "",
				DEFAULT: true,
			};

	const selectedRowLifecycleStatusObject =
		getDisabledStateFromLifecycleStatuses({
			documentLifecycleStatus: selectedRowData?.documentLifecycleStatus,
			emailLifecycleStatus: selectedRowData?.emailLifecycleStatus,
			matterNoteLifecycleStatus:
				selectedRowData?.matterNoteLifecycleStatus,
		});
	const itemLifecycleStatusObject =
		documentPreview?.documentLifecycleStatusObject ??
			selectedRowLifecycleStatusObject ?? {
				IN_TRASH: false,
				disabled: false,
				disabledText: "",
				DEFAULT: true,
			};

	const previewLockedBySub =
		documentPreview?.lockedBySub ?? selectedRowData?.lockedBySub;
	const previewInTrashOrArchive =
		matterOrFolderLifecycleStatusObject.IN_TRASH ||
		matterOrFolderLifecycleStatusObject.IN_ARCHIVE ||
		itemLifecycleStatusObject.IN_TRASH ||
		itemLifecycleStatusObject.IN_ARCHIVE;
	const previewInTrashOrArchiveDisabledText =
		matterOrFolderLifecycleStatusObject.disabledText ||
		itemLifecycleStatusObject.disabledText;

	const docCanBeOpenedInLauncher =
		launcherInstalled &&
		(!previewLockedBySub || previewLockedBySub === cognitoSub) &&
		!previewInTrashOrArchive;

	const openDocumentHandler = () =>
		dispatch(openDocumentInLauncher(selectedRowData.id, cognitoSub));
	const downloadCopyOfDocument = () =>
		dispatch(getSignedUrlForDocumentAndDownload(selectedRowData.id));
	const openEmailHandler = () =>
		dispatch(getSignedUrlForEmailAndDownload(selectedRowData.id));

	const openPropertiesForDocPreview = () =>
		window
			.open(
				`https://${config.domain}/docs/${selectedRowData.id}`,
				"_blank"
			)
			.focus();

	const openEditMatterNote = () =>
		window
			.open(
				`https://${config.domain}/notes/${selectedRowData.id}`,
				"_blank"
			)
			.focus();

	const openMatterHandler = () =>
		window
			.open(
				`https://${config.domain}/matters/${selectedRowData.id}/docs/`,
				"_blank"
			)
			.focus();

	const emailShortNote = documentPreview?.notes ?? selectedRowData?.notes;

	const shortNoteFromEmailPreview = () => {
		dispatch(
			openGlobalDialog(
				{
					label: emailShortNote
						? "Edit Email Short Note"
						: "Add Email Short Note",
					value: emailShortNote,
					submitButtonLabel: "Update Note",
				},
				(state) => {
					dispatch(
						updateEmailNote({
							emailID: selectedRowData.id,
							value: state.value,
							showSuccessSnackbar: true,
						})
					);
				},
				(state) => ({
					canSubmit: state.value !== emailShortNote,
					erroMessage: "",
				})
			)
		);
	};

	const generateEmailPreviewContent = (previewText) => (
		<div>
			<div className="emailPreviewInfoContainer">
				<label className="emailPreviewInfoLabel">From : </label>
				<span className="emailPreviewInfoValue">
					{documentPreview?.emailFrom ?? selectedRowData.emailFrom}
				</span>
				<label className="emailPreviewInfoLabel">To : </label>
				<span className="emailPreviewInfoValue">
					{documentPreview?.emailTo ?? selectedRowData.emailTo}
				</span>
				<label className="emailPreviewInfoLabel">Date : </label>
				<span className="emailPreviewInfoValue">
					{shortDateFormat(
						documentPreview?.date ?? selectedRowData.date
					)}
				</span>
				{emailShortNote ? (
					<>
						<label className="emailPreviewInfoLabel">
							Short Note :{" "}
						</label>
						<span className="emailPreviewInfoValue">
							{emailShortNote}
						</span>
					</>
				) : null}
			</div>
			<div>
				<p className="preview-text">{previewText}</p>
			</div>
		</div>
	);

	const generateDocumentPreviewContent = (documentPreviewResponse) => {
		if (selectedRowData.type === "Matter") {
			return generateMatterPreviewContent(documentPreviewResponse);
		}

		if (documentPreviewResponse) {
			var previewText;
			// need to catch empty string or the object will get set as the preview text and it'll crash the front end
			if (
				documentPreviewResponse.content ||
				documentPreviewResponse.content === ""
			) {
				previewText = sanitizePreviewTextContent(
					documentPreviewResponse
				);
			} else {
				previewText = documentPreviewResponse;
			}

			const isRichPreview =
				documentPreviewResponse.url ||
				documentPreviewResponse.pdfUrl ||
				documentPreviewResponse.docxUrl ||
				documentPreviewResponse.excelUrl;

			if (isRichPreview && documentPreviewResponse.noS3Object) {
				return (
					<div className="previewOpenDocumentContainer">
						<div className="previewOpenDocumentCenter">
							<p>Rich Preview Not Available</p>
						</div>
					</div>
				);
			} else if (documentPreviewResponse.url) {
				return (
					<div className="preview-image">
						<img
							alt="documentImagePreview"
							src={documentPreviewResponse.url}
						/>
					</div>
				);
			} else if (documentPreviewResponse.pdfUrl) {
				return (
					<div className="preview-pdf">
						<iframe src={documentPreviewResponse.pdfUrl}></iframe>
					</div>
				);
			} else if (documentPreviewResponse.docxUrl) {
				return <DocxPreview url={documentPreviewResponse.docxUrl} />;
			} else if (documentPreviewResponse.excelUrl) {
				return <ExcelPreview url={documentPreviewResponse.excelUrl} />;
			} else if (
				documentPreviewResponse.content &&
				previewText.length > 0
			) {
				if (selectedRowData.type === "File") {
					return (
						<div>
							<p className="preview-text">{previewText}</p>
						</div>
					);
				} else if (selectedRowData.type === "Note") {
					if (documentPreviewResponse.noteContentType === "html") {
						return (
							<div
								className="NoteHtmlContent"
								style={{ padding: 16 }}
								dangerouslySetInnerHTML={{
									__html: DOMPurify.sanitize(
										documentPreviewResponse.content
									),
								}}
							/>
						);
					}
					return (
						<div>
							<p className="preview-text">{previewText}</p>
						</div>
					);
				} else if (selectedRowData.type === "Email") {
					return generateEmailPreviewContent(previewText);
				}
			}
		}
		return (
			<div className="previewOpenDocumentContainer">
				<div className="previewOpenDocumentCenter">
					<p>Preview Not Available</p>
					{selectedRowData.type === "File" ? (
						docCanBeOpenedInLauncher ? (
							<ButtonLink onClick={openDocumentHandler}>
								Open Document
							</ButtonLink>
						) : (
							<ButtonLink onClick={downloadCopyOfDocument}>
								Download a Copy of Document
							</ButtonLink>
						)
					) : null}
					{selectedRowData.type === "Email" ? (
						<ButtonLink onClick={openEmailHandler}>
							Download Email
						</ButtonLink>
					) : null}
					{selectedRowData.type === "Note" ? (
						<ButtonLink onClick={openEditMatterNote}>
							Edit Note
						</ButtonLink>
					) : null}
				</div>
			</div>
		);
	};

	if (aiMode && openPane) {
		const documentName = selectedRowData.filename;
		return (
			<DocumentAISlidingPane
				isOpen={openPane}
				onRequestClose={closeRightPane}
				filename={documentName}
				closePane={closeRightPane}
				openProperties={openPropertiesForDocPreview}
				openDocument={openDocumentHandler}
				downloadCopyOfDocument={downloadCopyOfDocument}
				isFavorite={isFavorite}
				setFavorite={handleSetFavorite}
				favoriteLoading={favoriteLoading}
				inTrashOrArchive={previewInTrashOrArchive}
				inTrashOrArchiveDisabledText={
					previewInTrashOrArchiveDisabledText
				}
				headerExtraInfoDisplay={headerExtraInfoDisplay}
				headerExtraInfoTooltip={headerExtraInfoTooltip}
				documentID={selectedRowData.id}
				setHeaderExtraInfo={setHeaderExtraInfo}
				docCanBeOpenedInLauncher={docCanBeOpenedInLauncher}
				matterID={matterID}
			/>
		);
	}

	return (
		<SlidingPaneWrapper
			isOpen={openPane}
			onRequestClose={closeRightPane}
			zIndex={zIndex}
		>
			{openPane ? (
				<div>
					{selectedRowData.type === "File" ? (
						<DocumentPreviewHeader
							filename={selectedRowData.filename}
							versionLabel={
								showVersion
									? (selectedRowData.versionLabel ??
										(selectedRowData.versionNumber
											? `v${selectedRowData.versionNumber}`
											: undefined))
									: undefined
							}
							closePane={closeRightPane}
							openProperties={openPropertiesForDocPreview}
							openDocument={openDocumentHandler}
							downloadCopyOfDocument={downloadCopyOfDocument}
							isFavorite={isFavorite}
							setFavorite={handleSetFavorite}
							favoriteLoading={favoriteLoading}
							inTrashOrArchive={previewInTrashOrArchive}
							inTrashOrArchiveDisabledText={
								previewInTrashOrArchiveDisabledText
							}
							docCanBeOpenedInLauncher={docCanBeOpenedInLauncher}
							onTogglePlainText={
								[
									"PDF_INLINE",
									"DOCX_INLINE",
									"EXCEL_INLINE",
								].includes(
									getDocumentPreviewType(
										selectedRowData.filename
									)
								)
									? togglePlainText
									: undefined
							}
							toggleLabel={
								documentPreview &&
								(documentPreview.pdfUrl ||
									documentPreview.docxUrl ||
									documentPreview.excelUrl)
									? "Show Plain Text"
									: "Show Preview"
							}
						/>
					) : null}
					{selectedRowData.type === "Email" ? (
						<EmailHeader
							subjectline={selectedRowData.subjectline}
							originalFilename={selectedRowData.originalFilename}
							closePane={closeRightPane}
							openEmailShortNoteEditor={shortNoteFromEmailPreview}
							shortNoteValue={emailShortNote}
							openEmail={openEmailHandler}
							isEditNoteDisabled={
								selectedRowData.emailLifecycleStatus ===
									TRASH_LIFECYCLE_STATUS ||
								selectedRowData.emailLifecycleStatus ===
									ARCHIVE_LIFECYCLE_STATUS ||
								selectedRowData
									.matterOrFolderLifecycleStatusObject
									?.disabled
							}
							disabledTextForShortNote={
								selectedRowData
									.matterOrFolderLifecycleStatusObject
									?.disabled
									? selectedRowData
											.matterOrFolderLifecycleStatusObject
											.disabledText
									: selectedRowData.emailLifecycleStatus ===
										  ARCHIVE_LIFECYCLE_STATUS
										? "Not allowed for an Email that is in the Archive."
										: selectedRowData.emailLifecycleStatus ===
											  TRASH_LIFECYCLE_STATUS
											? "Not allowed for an Email that is in the Trash Bin."
											: ""
							}
						/>
					) : null}
					{selectedRowData.type === "Note" ? (
						<NoteHeader
							name={selectedRowData.name}
							closePane={closeRightPane}
							openEditMatterNote={openEditMatterNote}
						/>
					) : null}
					{selectedRowData.type === "Matter" ? (
						<MatterHeader
							name={selectedRowData.name}
							closePane={closeRightPane}
							openMatter={openMatterHandler}
						/>
					) : null}
				</div>
			) : null}
			{previewLoading ? (
				<Loading />
			) : (
				<div className="preview-content">
					{generateDocumentPreviewContent(documentPreview)}
				</div>
			)}
		</SlidingPaneWrapper>
	);
}

export default DocumentSlidingPane;
